import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Users,
  FileText,
  Clock,
  Activity,
  Target,
  AppWindow,
  ExternalLink,
  ArrowRight,
  Building2,
  Lock,
  ShieldAlert,
  BookOpen,
  Network,
  Scale,
  Wallet,
  Briefcase,
  Sparkles,
  AlertTriangle,
  Inbox as InboxIcon,
  Library,
  FileSignature
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { mockMetrics } from '../mock/mockData';
import HotLeadNotifier from './HotLeadNotifier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOLUTION_TILES = [
  { path: '/audit-fortress', icon: Building2, label: 'Audit Fortress', sub: 'RBI Pre-Audit · Tier-1', color: 'from-indigo-700 to-blue-700' },
  { path: '/dpdp-readiness', icon: Lock, label: 'DPDP Readiness 2026', sub: 'Nov 2026 deadline tracker', color: 'from-emerald-700 to-teal-700' },
  { path: '/frm-guardrail', icon: ShieldAlert, label: 'FRM Guardrail', sub: 'Real-time anti-fraud', color: 'from-rose-700 to-red-700' },
  { path: '/rule-cockpit', icon: BookOpen, label: 'Rule Cockpit', sub: 'Big-10 regulators', color: 'from-amber-600 to-orange-600' },
  { path: '/ai-orchestration', icon: Network, label: 'AI Orchestration', sub: '70+ unified APIs', color: 'from-blue-800 to-violet-700' },
  { path: '/lead-hub', icon: InboxIcon, label: 'Lead Generation Hub', sub: 'Multi-channel + Ask Sanjeev', color: 'from-fuchsia-700 to-pink-700' },
  { path: '/knowledge-hub', icon: Library, label: 'Knowledge Hub', sub: 'Edtech-grade how-tos', color: 'from-cyan-700 to-blue-700' },
  { path: '/contracts-hub', icon: FileSignature, label: 'Contracts Hub (CMS)', sub: 'RBI 10-Apr-2026 ready', color: 'from-slate-800 to-blue-900' },
  { path: '/legal-tech', icon: Scale, label: 'Legal Tech Hub', sub: 'Writs · Contracts · Templates', color: 'from-slate-800 to-zinc-700' },
  { path: '/finance', icon: Wallet, label: 'Finance Module', sub: 'Sanmiggrc Finance', color: 'from-cyan-700 to-blue-700' },
  { path: '/consultancy-hub', icon: Briefcase, label: 'Consultancy Hub', sub: 'Pan-India specialists', color: 'from-violet-700 to-fuchsia-700' },
  { path: '/strategic-intelligence', icon: Sparkles, label: 'Strategic Intelligence', sub: '$1B Brief · Apr 2026', color: 'from-blue-900 to-indigo-800' }
];

const ICON_MAP = {
  Calculator: Wallet,
  ShieldCheck: ShieldAlert,
  Network,
  Lock,
  ShieldAlert,
  BookOpen,
  Building2,
  TrendingUp: Sparkles,
  AppWindow
};

const Dashboard = () => {
  const [connectedApps, setConnectedApps] = useState([]);
  const [rulesStats, setRulesStats] = useState(null);
  const [dpdp, setDpdp] = useState(null);

  useEffect(() => {
    axios.get(`${API}/connected-apps`, { params: { enabled_only: true } }).then((r) => setConnectedApps(r.data)).catch(() => {});
    axios.get(`${API}/rules/stats`).then((r) => setRulesStats(r.data)).catch(() => {});
    axios.get(`${API}/dpdp/readiness`).then((r) => setDpdp(r.data)).catch(() => {});
  }, []);

  const getDate = () => new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-violet-900 text-white px-8 py-7">
        <p className="text-xs uppercase tracking-widest text-white/60 mb-1">{getDate()}</p>
        <h1 className="text-3xl font-bold">{getGreeting()}, Sanjeev</h1>
        <p className="text-sm text-white/80 mt-1.5 max-w-3xl">
          Sanmiggrc Technologies — India's single point of contact for enterprise GRC, AI Governance, AML / FRM,
          DPDP readiness and regulatory intelligence.
        </p>
      </div>

      {/* Main Content */}
      <div className="p-8 space-y-6">
        <HotLeadNotifier />

        {/* AI Brief */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="ai-brief">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span>AI Compliance Brief</span>
              <Badge className="bg-blue-100 text-blue-700">Live</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">
              {rulesStats?.high_impact ?? '—'} high-impact circulars active across {rulesStats ? Object.keys(rulesStats.by_regulator).length : '—'} regulators.
              DPDP readiness at {dpdp?.score ?? '—'}% with {dpdp?.days_left ?? '—'} days to the November-2026 deadline.
              {' '}{connectedApps.length} partner workspaces connected via Sentinel Gateway. 185 AI agents running with zero kill-switch escalations in the last 4 hours.
            </p>
          </CardContent>
        </Card>

        {/* Top metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="hover:shadow-md transition-shadow" data-testid="kpi-active-regulations">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Users className="w-7 h-7 text-blue-600" />
                <Badge variant="secondary">{mockMetrics.activeRegulations.change}</Badge>
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900">{mockMetrics.activeRegulations.value}</p>
              <p className="text-xs text-gray-600 mt-1">Active Regulations</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow" data-testid="kpi-aml">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <FileText className="w-7 h-7 text-rose-600" />
                <Badge variant="secondary">{mockMetrics.amlAlerts.change}</Badge>
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900">{mockMetrics.amlAlerts.value}</p>
              <p className="text-xs text-gray-600 mt-1">AML Alerts (24h)</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow" data-testid="kpi-txn">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Clock className="w-7 h-7 text-purple-600" />
                <Badge variant="secondary">{mockMetrics.transactionsMonitored.change}</Badge>
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900">{mockMetrics.transactionsMonitored.value}</p>
              <p className="text-xs text-gray-600 mt-1">Transactions Monitored</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow" data-testid="kpi-compliance">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Target className="w-7 h-7 text-emerald-600" />
                <Badge variant="secondary">{mockMetrics.complianceScore.change}</Badge>
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900">{mockMetrics.complianceScore.value}</p>
              <p className="text-xs text-gray-600 mt-1">Compliance Score</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow" data-testid="kpi-agents">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Activity className="w-7 h-7 text-orange-600" />
                <Badge variant="secondary">{mockMetrics.aiAgentsActive.change}</Badge>
              </div>
              <p className="text-2xl font-bold mt-3 text-gray-900">{mockMetrics.aiAgentsActive.value}</p>
              <p className="text-xs text-gray-600 mt-1">AI Agents Active</p>
            </CardContent>
          </Card>
        </div>

        {/* Solution Suite */}
        <Card data-testid="solution-suite">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <span>Sanmiggrc Solution Suite</span>
              </span>
              <Badge variant="outline">{SOLUTION_TILES.length} modules</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {SOLUTION_TILES.map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.path}
                    to={t.path}
                    data-testid={`tile-${t.path.replace('/', '')}`}
                    className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    <div className={`bg-gradient-to-br ${t.color} p-3 text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">{t.label}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate">{t.sub}</p>
                      <div className="flex items-center text-[11px] text-blue-700 font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        Launch <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* DPDP + Rules + System Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card data-testid="dpdp-snapshot">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center space-x-2">
                  <Lock className="w-5 h-5 text-emerald-600" />
                  <span>DPDP Readiness</span>
                </span>
                <Link to="/dpdp-readiness">
                  <Badge variant="outline" className="cursor-pointer hover:border-emerald-400">Open</Badge>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dpdp?.score ?? '—'}<span className="text-base text-gray-500">%</span></p>
              <p className="text-xs text-gray-500 mb-3">{dpdp?.days_left ?? '—'} days to 01-Nov-2026</p>
              <Progress value={dpdp?.score ?? 0} className="h-2" />
              <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                <span>{dpdp?.open_actions ?? 0} open actions</span>
                <span>{dpdp?.evidence_uploaded ?? 0} evidence files</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="rules-snapshot">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                  <span>Rule Cockpit</span>
                </span>
                <Link to="/rule-cockpit">
                  <Badge variant="outline" className="cursor-pointer hover:border-amber-400">Open</Badge>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{rulesStats?.total ?? '—'}</p>
              <p className="text-xs text-gray-500 mb-3">tracked rules · {rulesStats?.high_impact ?? 0} high-impact</p>
              <div className="flex flex-wrap gap-1">
                {rulesStats &&
                  Object.entries(rulesStats.by_regulator).slice(0, 8).map(([r, c]) => (
                    <Badge key={r} variant="outline" className="text-[10px]">
                      {r} · {c}
                    </Badge>
                  ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="system-overview">
            <CardHeader>
              <CardTitle>System Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Compliance Progress</span>
                    <span className="text-xs font-semibold text-gray-900">98.5%</span>
                  </div>
                  <Progress value={98.5} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Active Audits</span>
                    <span className="text-xs font-semibold text-gray-900">{mockMetrics.activeAudits.value}</span>
                  </div>
                  <Progress value={75} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Lead Conversion</span>
                    <span className="text-xs font-semibold text-gray-900">34%</span>
                  </div>
                  <Progress value={34} className="h-2" />
                </div>
                <div className="pt-3 border-t border-gray-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Regulatory Alerts</span>
                    <Badge variant="destructive">{mockMetrics.regulatoryAlerts.value}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Inbound Leads</span>
                    <Badge variant="secondary">{mockMetrics.leadsInbound.value}</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Connected Apps */}
        <Card data-testid="dashboard-connected-apps">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <AppWindow className="w-5 h-5 text-blue-600" />
                <span>Connected Apps</span>
                <Badge variant="secondary">{connectedApps.length}</Badge>
              </CardTitle>
              <Link to="/connected-apps">
                <Badge variant="outline" className="cursor-pointer hover:border-blue-400">
                  Manage <ArrowRight className="w-3 h-3 ml-1 inline" />
                </Badge>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {connectedApps.length === 0 ? (
              <p className="text-sm text-gray-500">No apps connected yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {connectedApps.map((appItem) => {
                  const Icon = ICON_MAP[appItem.icon] || AppWindow;
                  return (
                    <Link
                      key={appItem.slug}
                      to="/connected-apps"
                      data-testid={`dashboard-app-${appItem.slug}`}
                      className="group block border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all bg-white"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 truncate group-hover:text-blue-700">
                            {appItem.name}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate mt-0.5">
                            {appItem.url.replace(/^https?:\/\//, '')}
                          </p>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
